import React from "react";
import {
  GridCellParams,
  GridRenderCellParams,
  GridValueFormatterParams,
} from "@mui/x-data-grid";
import { Analysis, FormItem, ItemType } from "../../types";
import { Box, Tooltip, Typography } from "@mui/material";
import ArrowCircleDownIcon from "@mui/icons-material/ArrowCircleDown";
import ArrowCircleUpIcon from "@mui/icons-material/ArrowCircleUp";

function LowerIcon() {
  return (
    <ArrowCircleDownIcon
      sx={{ color: "var(--color-red)", width: "20px", ml: -0.5 }}
    />
  );
}

function HigherIcon() {
  return (
    <ArrowCircleUpIcon
      sx={{ color: "var(--color-green)", width: "20px", ml: -0.5 }}
    />
  );
}

const safeFixed = (value: number) =>
  Number.isFinite(value) ? value.toFixed(2) : "--";
const safeFixedPercent = (value: number) =>
  Number.isFinite(value) ? (value * 100).toFixed(0) : "--";

/** Renders a question label cell with single-line CSS truncation +
 * MUI tooltip showing the full text on hover. (FL-209) */
const renderQuestionCell = (params: GridRenderCellParams) => (
  <Tooltip title={(params.value as string) || ""} placement="top">
    <Box
      sx={{
        width: "100%",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {params.value}
    </Box>
  </Tooltip>
);

/** Shared Question column definition with truncation + hover tooltip. */
export const QuestionColumnDef = {
  headerName: "Question",
  field: "question",
  flex: 400,
  renderCell: renderQuestionCell,
};

export const IndividualSelfPeerColumns = [
  {
    ...QuestionColumnDef,
  },
  {
    headerName: "Self",
    field: "self",
    flex: 66,
    valueFormatter: ({ value }: GridValueFormatterParams<number>) =>
      safeFixed(value),
  },
  {
    headerName: "Received",
    field: "peers",
    flex: 66,
    valueFormatter: ({ value }: GridValueFormatterParams<number>) =>
      safeFixed(value),
  },
  {
    headerName: "Gap",
    field: "gap",
    flex: 66,
    valueFormatter: ({ value }: GridValueFormatterParams<number>) =>
      safeFixed(value),
    cellClassName: ({ value }: GridCellParams<number>) =>
      value && value > 0
        ? "alignment-positive"
        : value && value < 0
          ? "alignment-negative"
          : "",
  },
];

/** Released results view uses same columns (Received is already the header name) */
export const IndividualSelfPeerReleasedColumns = IndividualSelfPeerColumns;

export const IndividualGivenColumns = [
  {
    ...QuestionColumnDef,
  },
  {
    headerName: "Given",
    field: "given",
    flex: 66,
    valueFormatter: ({ value }: GridValueFormatterParams<number>) =>
      safeFixed(value),
  },
];

export const IndividualSelfPeerOutlierColumns = [
  {
    headerName: "Name",
    field: "label",
    flex: 400,
  },
  {
    headerName: "Self",
    field: "self",
    flex: 50,
    minWidth: 75,
    valueFormatter: ({ value }: GridValueFormatterParams<number>) =>
      safeFixed(value),
    renderCell: (analysis: GridRenderCellParams<Analysis>) => (
      <>
        <Typography padding={1}>{safeFixed(analysis.row.self)}</Typography>
        {Number.isFinite(analysis.row.self) && analysis.row.self <= 4.0 && (
          <LowerIcon />
        )}
      </>
    ),
  },
  {
    headerName: "Received",
    field: "others",
    flex: 50,
    minWidth: 75,
    valueFormatter: ({ value }: GridValueFormatterParams<number>) =>
      safeFixed(value),
    renderCell: (analysis: GridRenderCellParams<Analysis>) => (
      <>
        <Typography padding={1}>{safeFixed(analysis.row.others)}</Typography>
        {Number.isFinite(analysis.row.others) && analysis.row.others <= 4.0 && (
          <LowerIcon />
        )}
      </>
    ),
  },
  {
    headerName: "Gap",
    field: "gap",
    flex: 50,
    minWidth: 75,
    valueFormatter: ({ value }: GridValueFormatterParams<number>) =>
      safeFixed(value),
    renderCell: (analysis: GridRenderCellParams<Analysis>) => (
      <>
        <Typography padding={1}>{safeFixed(analysis.row.gap)}</Typography>
        {Number.isFinite(analysis.row.gap) && analysis.row.gap <= -1.5 && (
          <LowerIcon />
        )}
        {Number.isFinite(analysis.row.gap) && analysis.row.gap >= 1.5 && (
          <HigherIcon />
        )}
      </>
    ),
  },
];

export const TeamSelfPeerOutlierColumns = [
  {
    headerName: "Team",
    field: "label",
    flex: 400,
  },
  {
    headerName: "Self",
    field: "self",
    flex: 66,
    minWidth: 75,
    valueFormatter: ({ value }: GridValueFormatterParams<number>) =>
      safeFixed(value),
    renderCell: (analysis: GridRenderCellParams<Analysis>) => (
      <>
        <Typography padding={1}>{safeFixed(analysis.row.self)}</Typography>
        {Number.isFinite(analysis.row.self) && analysis.row.self <= 4.0 && (
          <LowerIcon />
        )}
      </>
    ),
  },
  {
    headerName: "Received",
    field: "others",
    flex: 66,
    minWidth: 75,
    valueFormatter: ({ value }: GridValueFormatterParams<number>) =>
      safeFixed(value),
    renderCell: (analysis: GridRenderCellParams<Analysis>) => (
      <>
        <Typography padding={1}>{safeFixed(analysis.row.others)}</Typography>
        {Number.isFinite(analysis.row.others) && analysis.row.others <= 4.0 && (
          <LowerIcon />
        )}
      </>
    ),
  },
  {
    headerName: "Gap",
    field: "gap",
    flex: 66,
    minWidth: 75,
    valueFormatter: ({ value }: GridValueFormatterParams<number>) =>
      safeFixed(value),
    renderCell: (analysis: GridRenderCellParams<Analysis>) => (
      <>
        <Typography padding={1}>{safeFixed(analysis.row.gap)}</Typography>
        {Number.isFinite(analysis.row.gap) && analysis.row.gap <= -1.5 && (
          <LowerIcon />
        )}
        {Number.isFinite(analysis.row.gap) && analysis.row.gap >= 1.5 && (
          <HigherIcon />
        )}
      </>
    ),
  },
];

export const GroupSelfPeerColumns = [
  {
    ...QuestionColumnDef,
  },
  {
    headerName: "User",
    field: "user",
    flex: 150,
  },
  {
    headerName: "Self",
    field: "self",
    flex: 66,
    valueFormatter: ({ value }: GridValueFormatterParams<number>) =>
      safeFixed(value),
  },
  {
    headerName: "Received",
    field: "peers",
    flex: 66,
    valueFormatter: ({ value }: GridValueFormatterParams<number>) =>
      safeFixed(value),
  },
  {
    headerName: "Gap",
    field: "gap",
    flex: 66,
    valueFormatter: ({ value }: GridValueFormatterParams<number>) =>
      safeFixed(value),
  },
];

export const GroupGivenColumns = [
  {
    ...QuestionColumnDef,
  },
  {
    headerName: "User",
    field: "user",
    flex: 150,
  },
  {
    headerName: "Given",
    field: "given",
    flex: 66,
    valueFormatter: ({ value }: GridValueFormatterParams<number>) =>
      safeFixed(value),
  },
];

export const AverageSelfPeerColumns = [
  {
    headerName: "User",
    field: "user",
    flex: 400,
  },
  {
    headerName: "Self",
    field: "self",
    flex: 66,
    valueFormatter: ({ value }: GridValueFormatterParams<number>) =>
      safeFixed(value),
  },
  {
    headerName: "Received",
    field: "others",
    flex: 66,
    valueFormatter: ({ value }: GridValueFormatterParams<number>) =>
      safeFixed(value),
  },
  {
    headerName: "Gap",
    field: "gap",
    flex: 66,
    valueFormatter: ({ value }: GridValueFormatterParams<number>) =>
      safeFixed(value),
    cellClassName: ({ value }: GridCellParams<number>) =>
      value && value > 0
        ? "alignment-positive"
        : value && value < 0
          ? "alignment-negative"
          : "",
  },
];

export const AverageGivenColumns = [
  {
    headerName: "User",
    field: "user",
    flex: 400,
  },
  {
    headerName: "Given",
    field: "given",
    flex: 66,
    valueFormatter: ({ value }: GridValueFormatterParams<number>) =>
      safeFixed(value),
  },
];

//===== Non-peer responses

export const IndividualColumns = [
  {
    ...QuestionColumnDef,
  },
  {
    headerName: "Value",
    field: "peers",
    flex: 350,
    valueFormatter: ({ value }: GridValueFormatterParams<number>) =>
      safeFixed(value),
  },
];

export const IndividualOutlierColumns = [
  {
    headerName: "Name",
    field: "label",
    flex: 400,
  },
  {
    headerName: "Value",
    field: "others",
    flex: 350,
    minWidth: 75,
    valueFormatter: ({ value }: GridValueFormatterParams<number>) =>
      safeFixed(value),
    renderCell: (analysis: GridRenderCellParams<Analysis>) => (
      <>
        <Typography padding={1}>{safeFixed(analysis.row.others)}</Typography>
        {Number.isFinite(analysis.row.others) && analysis.row.others <= 4.0 && (
          <LowerIcon />
        )}
      </>
    ),
  },
];

export const TeamOutlierColumns = [
  {
    headerName: "Team",
    field: "label",
    flex: 400,
  },
  {
    headerName: "Value",
    field: "others",
    flex: 350,
    minWidth: 75,
    valueFormatter: ({ value }: GridValueFormatterParams<number>) =>
      safeFixed(value),
    renderCell: (analysis: GridRenderCellParams<Analysis>) => (
      <>
        <Typography padding={1}>{safeFixed(analysis.row.others)}</Typography>
        {Number.isFinite(analysis.row.others) && analysis.row.others <= 4.0 && (
          <LowerIcon />
        )}
      </>
    ),
  },
];

export const AverageColumns = [
  {
    headerName: "User",
    field: "user",
    flex: 400,
  },
  {
    headerName: "Value",
    field: "others",
    flex: 350,
    valueFormatter: ({ value }: GridValueFormatterParams<number>) =>
      safeFixed(value),
  },
];

export const AlignmentColumns = [
  {
    headerName: "Section",
    field: "section",
    flex: 400,
  },
  {
    headerName: "Alignment",
    field: "alignment",
    flex: 66,
    cellClassName: ({ value }: GridCellParams<number>) =>
      value && value >= 1.15
        ? "alignment-positive"
        : value && value <= 0.85
          ? "alignment-negative"
          : "",
    valueFormatter: ({ value }: GridValueFormatterParams<number>) =>
      safeFixed(value),
    renderCell: (
      row: GridRenderCellParams<{ section: string; alignment: number }>,
    ) => (
      <Typography padding={1}>
        {safeFixedPercent(row.row.alignment) + "%"}
      </Typography>
    ),
  },
];

export const GroupColumns = [
  {
    ...QuestionColumnDef,
  },
  {
    headerName: "User",
    field: "user",
    flex: 150,
  },
  {
    headerName: "Value",
    field: "peers",
    flex: 200,
    valueFormatter: ({ value }: GridValueFormatterParams<number>) =>
      safeFixed(value),
  },
];

// === Free text responses

export const IndividualTextColumns = [
  {
    headerName: "Sender",
    field: "respondent",
    flex: 100,
  },
  {
    headerName: "Response",
    field: "comment",
    flex: 400,
  },
];

export const IndividualGivenTextColumns = [
  {
    headerName: "Recipient",
    field: "recipient",
    flex: 100,
  },
  {
    headerName: "Comment",
    field: "comment",
    flex: 400,
  },
];

export const GroupTextColumns = [
  {
    headerName: "Recipient",
    field: "recipient",
    flex: 100,
  },
  {
    headerName: "Sender",
    field: "respondent",
    flex: 100,
  },
  {
    headerName: "Response",
    field: "comment",
    flex: 400,
  },
];

// === Point allocation responses

export const IndividualPointColumns = [
  {
    ...QuestionColumnDef,
  },
  {
    headerName: "Sender",
    field: "sender",
    flex: 200,
  },
  {
    headerName: "Points",
    field: "value",
    flex: 200,
  },
];

export const GroupPointColumns = [
  {
    ...QuestionColumnDef,
  },
  {
    headerName: "Recipient",
    field: "user",
    flex: 150,
  },
  {
    headerName: "Sender",
    field: "sender",
    flex: 150,
  },
  {
    headerName: "Points",
    field: "value",
    flex: 200,
  },
];

// === Yes/No question responses
export const IndividualSelfPeerYesNoColumns = [
  {
    ...QuestionColumnDef,
  },
  {
    headerName: "Self",
    field: "self",
    flex: 66,
    valueFormatter: ({ value }: GridValueFormatterParams<number>) =>
      value === 1 ? "Yes" : value === 0 ? "No" : "--",
  },
  {
    headerName: "Received",
    field: "peers",
    flex: 66,
    valueFormatter: ({ value }: GridValueFormatterParams<number>) =>
      value !== undefined ? `Yes ${(value * 100).toFixed(0)}%` : "--",
  },
  {
    headerName: "Gap",
    field: "gap",
    flex: 66,
    valueFormatter: ({ value }: GridValueFormatterParams<number>) =>
      value !== undefined ? `${(value * 100).toFixed(0)}%` : "--",
    cellClassName: ({ value }: GridCellParams<number>) =>
      value && value > 0
        ? "alignment-positive"
        : value && value < 0
          ? "alignment-negative"
          : "",
  },
];

export const GroupSelfPeerYesNoColumns = [
  {
    ...QuestionColumnDef,
  },
  {
    headerName: "User",
    field: "user",
    flex: 150,
  },
  {
    headerName: "Self",
    field: "self",
    flex: 66,
    valueFormatter: ({ value }: GridValueFormatterParams<number>) =>
      value === 1 ? "Yes" : value === 0 ? "No" : "--",
  },
  {
    headerName: "Received",
    field: "peers",
    flex: 66,
    valueFormatter: ({ value }: GridValueFormatterParams<number>) =>
      value !== undefined ? `Yes ${(value * 100).toFixed(0)}%` : "--",
  },
  {
    headerName: "Gap",
    field: "gap",
    flex: 66,
    valueFormatter: ({ value }: GridValueFormatterParams<number>) =>
      value !== undefined ? `${(value * 100).toFixed(0)}%` : "--",
  },
];

export const IndividualYesNoColumns = [
  {
    ...QuestionColumnDef,
  },
  {
    headerName: "Value",
    field: "peers",
    flex: 350,
    valueFormatter: ({ value }: GridValueFormatterParams<number>) =>
      value === 1
        ? "Yes"
        : value === 0
          ? "No"
          : value !== undefined
            ? `Yes ${(value * 100).toFixed(0)}%`
            : "--",
  },
];

export const GroupYesNoColumns = [
  {
    ...QuestionColumnDef,
  },
  {
    headerName: "User",
    field: "user",
    flex: 150,
  },
  {
    headerName: "Value",
    field: "peers",
    flex: 200,
    valueFormatter: ({ value }: GridValueFormatterParams<number>) =>
      value === 1
        ? "Yes"
        : value === 0
          ? "No"
          : value !== undefined
            ? `Yes ${(value * 100).toFixed(0)}%`
            : "--",
  },
];

// ── Template-based rater type detection ─────────────────────────────────────

/** Returns true if the template's choice items span more than one recipientType. */
export function hasMultipleRaterTypes(items: FormItem[]): boolean {
  const choiceItems = items.filter((it) => it.itemType === ItemType.Choice);
  const types = new Set(choiceItems.map((it) => it.recipientType));
  return types.size > 1;
}

// ── Per-rater-type column definitions ───────────────────────────────────────

const OthersInstructorCol = {
  headerName: "Instructors",
  field: "othersInstructor",
  flex: 66,
  valueFormatter: ({ value }: GridValueFormatterParams<number>) => safeFixed(value),
  headerClassName: "rater-subordinate",
  cellClassName: "rater-subordinate",
};

const OthersStudentCol = {
  headerName: "Students",
  field: "othersStudent",
  flex: 66,
  valueFormatter: ({ value }: GridValueFormatterParams<number>) => safeFixed(value),
  headerClassName: "rater-subordinate",
  cellClassName: "rater-subordinate",
};

const OthersLeadCol = {
  headerName: "Leads",
  field: "othersLead",
  flex: 66,
  valueFormatter: ({ value }: GridValueFormatterParams<number>) => safeFixed(value),
  headerClassName: "rater-subordinate",
  cellClassName: "rater-subordinate",
};

const perRaterCols = [OthersInstructorCol, OthersStudentCol, OthersLeadCol];

// YesNo variants
const yesNoPercentFormatter = ({ value }: GridValueFormatterParams<number>) =>
  value === 1 ? "Yes" : value === 0 ? "No" : value !== undefined ? `Yes ${(value * 100).toFixed(0)}%` : "--";

const OthersInstructorYesNoCol = { ...OthersInstructorCol, valueFormatter: yesNoPercentFormatter, headerClassName: "rater-subordinate", cellClassName: "rater-subordinate" };
const OthersStudentYesNoCol = { ...OthersStudentCol, valueFormatter: yesNoPercentFormatter, headerClassName: "rater-subordinate", cellClassName: "rater-subordinate" };
const OthersLeadYesNoCol = { ...OthersLeadCol, valueFormatter: yesNoPercentFormatter, headerClassName: "rater-subordinate", cellClassName: "rater-subordinate" };

const perRaterYesNoCols = [OthersInstructorYesNoCol, OthersStudentYesNoCol, OthersLeadYesNoCol];

/** Insert per-rater-type columns after the "Received" column and apply styling. */
function insertPerRaterCols(baseCols: any[], raterCols: any[], receivedField: string) {
  const idx = baseCols.findIndex((c: any) => c.field === receivedField);
  if (idx === -1) return [...baseCols, ...raterCols];
  // Add received-primary styling to the Received column
  const styledReceived = {
    ...baseCols[idx],
    headerClassName: "received-primary",
    cellClassName: "received-primary",
  };
  return [
    ...baseCols.slice(0, idx),
    styledReceived,
    ...raterCols,
    ...baseCols.slice(idx + 1),
  ];
}

// ── Factory functions ───────────────────────────────────────────────────────

export function getIndividualSelfPeerColumns(hasMixedAuthors: boolean) {
  if (!hasMixedAuthors) return IndividualSelfPeerColumns;
  return insertPerRaterCols(IndividualSelfPeerColumns, perRaterCols, "peers");
}

export function getGroupSelfPeerColumns(hasMixedAuthors: boolean) {
  if (!hasMixedAuthors) return GroupSelfPeerColumns;
  return insertPerRaterCols(GroupSelfPeerColumns, perRaterCols, "peers");
}

export function getAverageSelfPeerColumns(hasMixedAuthors: boolean) {
  if (!hasMixedAuthors) return AverageSelfPeerColumns;
  return insertPerRaterCols(AverageSelfPeerColumns, perRaterCols, "others");
}

export function getIndividualSelfPeerYesNoColumns(hasMixedAuthors: boolean) {
  if (!hasMixedAuthors) return IndividualSelfPeerYesNoColumns;
  return insertPerRaterCols(IndividualSelfPeerYesNoColumns, perRaterYesNoCols, "peers");
}

export function getGroupSelfPeerYesNoColumns(hasMixedAuthors: boolean) {
  if (!hasMixedAuthors) return GroupSelfPeerYesNoColumns;
  return insertPerRaterCols(GroupSelfPeerYesNoColumns, perRaterYesNoCols, "peers");
}
